import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TracksForm = ({ tracks, onTracksChange, errors }) => {
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`setupWizard.tracks.${key}`, opts);

  const [newTrack, setNewTrack] = useState({ name: '', description: '', categories: [] });
  const [newCategory, setNewCategory] = useState('');

  const handleAddTrack = () => {
    if (!newTrack?.name?.trim()) return;

    const track = {
      id: Date.now(),
      name: newTrack?.name,
      description: newTrack?.description,
      categories: newTrack?.categories,
      order: tracks?.length
    };

    onTracksChange([...tracks, track]);
    setNewTrack({ name: '', description: '', categories: [] });
  };

  const handleRemoveTrack = (trackId) => {
    onTracksChange(tracks?.filter(t => t?.id !== trackId));
  };

  const handleMoveTrack = (index, direction) => {
    const newTracks = [...tracks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newTracks?.length) return;

    [newTracks[index], newTracks[targetIndex]] = [newTracks?.[targetIndex], newTracks?.[index]];
    newTracks?.forEach((track, idx) => track.order = idx);

    onTracksChange(newTracks);
  };

  const handleAddCategory = () => {
    if (!newCategory?.trim()) return;
    setNewTrack({
      ...newTrack,
      categories: [...newTrack?.categories, newCategory?.trim()]
    });
    setNewCategory('');
  };

  const handleRemoveCategory = (category) => {
    setNewTrack({
      ...newTrack,
      categories: newTrack?.categories?.filter(c => c !== category)
    });
  };

  const predefinedTracks = ['main', 'industry', 'workshop']?.map((key) => {
    const categories = t(`setupWizard.tracks.predefined.${key}.categories`, { returnObjects: true });
    return {
      name: k(`predefined.${key}.name`),
      description: k(`predefined.${key}.description`),
      categories: Array.isArray(categories) ? categories : []
    };
  });

  const handleUsePredefined = (predefined) => {
    setNewTrack({
      name: predefined?.name,
      description: predefined?.description,
      categories: [...(predefined?.categories || [])]
    });
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-muted/50 rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4">{k('predefinedTitle')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {predefinedTracks?.map((track, index) => (
            <button
              key={index}
              onClick={() => handleUsePredefined(track)}
              className="p-4 rounded-lg border border-border bg-card hover:bg-muted transition-all duration-250 text-start"
            >
              <h4 className="font-medium text-foreground mb-2">{track?.name}</h4>
              <p className="text-xs text-muted-foreground">{track?.description}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">{k('addNewTitle')}</h3>

        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={k('trackName.label')}
              type="text"
              placeholder={k('trackName.placeholder')}
              value={newTrack?.name}
              onChange={(e) => setNewTrack({ ...newTrack, name: e?.target?.value })}
              required
            />

            <Input
              label={k('trackDescription.label')}
              type="text"
              placeholder={k('trackDescription.placeholder')}
              value={newTrack?.description}
              onChange={(e) => setNewTrack({ ...newTrack, description: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {k('categories.label')}
            </label>
            <div className="flex gap-2 mb-3">
              <Input
                type="text"
                placeholder={k('categories.placeholder')}
                value={newCategory}
                onChange={(e) => setNewCategory(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddCategory())}
              />
              <Button
                variant="outline"
                onClick={handleAddCategory}
                iconName="Plus"
                disabled={!newCategory?.trim()}
              >
                {k('add')}
              </Button>
            </div>

            {newTrack?.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {newTrack?.categories?.map((category, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm"
                  >
                    {category}
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="hover:text-error transition-colors duration-250"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button
            variant="default"
            onClick={handleAddTrack}
            iconName="Plus"
            iconPosition="left"
            disabled={!newTrack?.name?.trim()}
          >
            {k('addTrack')}
          </Button>
        </div>
      </div>
      {tracks?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground">
            {k('configured', { count: tracks?.length })}
          </h3>

          <div className="space-y-3 md:space-y-4">
            {tracks?.map((track, index) => (
              <div
                key={track?.id}
                className="bg-card rounded-lg border border-border p-4 md:p-6"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-1">
                      {track?.name}
                    </h4>
                    {track?.description && (
                      <p className="text-sm text-muted-foreground">{track?.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveTrack(index, 'up')}
                      disabled={index === 0}
                      iconName="ChevronUp"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveTrack(index, 'down')}
                      disabled={index === tracks?.length - 1}
                      iconName="ChevronDown"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveTrack(track?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </div>
                </div>

                {track?.categories?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 caption">
                      {k('categoriesCaption')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {track?.categories?.map((category, catIndex) => (
                        <span
                          key={catIndex}
                          className="px-3 py-1.5 bg-muted text-foreground rounded-md text-sm"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {errors?.tracks && (
        <p className="text-sm text-error">{errors?.tracks}</p>
      )}
    </div>
  );
};

export default TracksForm;
